import api from './api';
import { getParamsUrl } from '@/stores/elemento_tipo/elemento_tipo_utils.js';
const ENTITY_ENDPOINT = 'elemento-tipo';

/**
 * Servicio para gestionar las operaciones relacionadas con Elemento Tipo
 */
export const elementoTipoService = {
  /**
   * Obtiene una lista paginada de tipos de producto
   * @param {Object} params - Parámetros de consulta (page, filters, etc.)
   * @returns {Promise<Object|null>} Los datos de la respuesta o null
   */
  async findAll(params = {}) {
    try {
      const result = await api.get(`/${ENTITY_ENDPOINT}?${getParamsUrl(params)}`);
      return result?.data || null;
    } catch (error) {
      console.error('Error al obtener tipos de producto:', error);
      throw error;
    }
  },

  /**
   * Busca un elemento tipo por su código
   * @param {string} codigo - Código del elemento tipo
   * @returns {Promise<Object|null>} El elemento tipo encontrado o null
   */
  async findByCodigo(codigo) {
    try {
      const result = await api.get(`/${ENTITY_ENDPOINT}/${codigo}`);
      return result?.data || null;
    } catch (error) {
      console.error(`Error al obtener elemento tipo ${codigo}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene los procesos por defecto de un elemento tipo para un módulo específico
   * @param {string} codigoElementoTipo - Código del elemento tipo
   * @param {string} modulo - Módulo específico
   * @returns {Promise<Array>} Array de procesos por defecto
   */
  // async findProcesosPorDefecto(codigoElementoTipo, modulo) {
  //   try {
  //     const result = await api.get(`/${ENTITY_ENDPOINT}/procesos-por-defecto/${codigoElementoTipo}/${modulo}`)
  //     return result?.data || []
  //   } catch (error) {
  //     console.error(`Error al obtener procesos por defecto para ${codigoElementoTipo}/${modulo}:`, error)
  //     throw error
  //   }
  // },

  /**
   * Obtiene todos los procesos asociados a un elemento tipo
   * @param {string} codigoElementoTipo - Código del elemento tipo
   * @returns {Promise<Array>} Array de procesos del elemento tipo
   */
  // async getProcesosPorSeccion(codigoElementoTipo) {
  //   try {
  //     const result = await api.get(`/${ENTITY_ENDPOINT}/${codigoElementoTipo}/procesos`)
  //     return result?.data.data || []
  //   } catch (error) {
  //     console.error(`Error al obtener procesos para elemento tipo ${codigoElementoTipo}:`, error)
  //     throw error
  //   }
  // },

  /**
   * Crea un nuevo elemento tipo
   * @param {Object} elementoTipo - Datos del elemento tipo a crear
   * @returns {Promise<Object|null>} El elemento tipo creado o null
   */
  async create(elementoTipo) {
    try {
      const result = await api.post(`/${ENTITY_ENDPOINT}`, elementoTipo);
      return result?.data || null;
    } catch (error) {
      console.error('Error al crear elemento tipo:', error);
      throw error;
    }
  },

  /**
   * Actualiza un elemento tipo existente
   * @param {string} codigo - Código del elemento tipo a actualizar
   * @param {Object} elementoTipo - Datos del elemento tipo a actualizar
   * @returns {Promise<Object|null>} El elemento tipo actualizado o null
   */
  async update(codigo, elementoTipo) {
    try {
      const result = await api.patch(`/${ENTITY_ENDPOINT}/${codigo}`, elementoTipo);
      return result?.data || null;
    } catch (error) {
      console.error(`Error al actualizar elemento tipo ${codigo}:`, error);
      throw error;
    }
  },

  /**
   * Obtiene todos los elementos tipo con detalles
   * @param {number} page - Número de página (opcional, por defecto 0)
   * @returns {Promise<Array>} Array de elementos tipo con detalles
   */
  // async getAllDetailed(page = 0) {
  //   try {
  //     const result = await api.get(`/${ENTITY_ENDPOINT}/detailed?page=${page}`)
  //     return result?.data || []
  //   } catch (error) {
  //     console.error('Error al obtener elementos tipo detallados:', error)
  //     throw error
  //   }
  // },

  /**
   * Crea procesos para un elemento tipo
   * @param {string} codigoElementoTipo - Código del elemento tipo
   * @param {Array} procesos - Array de procesos a crear
   * @returns {Promise<Object|null>} Resultado de la operación
   */
  async createProcesos(codigoElementoTipo, procesos) {
    try {
      const result = await api.post(`/${ENTITY_ENDPOINT}/${codigoElementoTipo}/procesos`, procesos);
      return result?.data || null;
    } catch (error) {
      console.error(`Error al crear procesos para elemento tipo ${codigoElementoTipo}:`, error);
      throw error;
    }
  },

  /**
   * Actualiza procesos de un elemento tipo
   * @param {string} codigoElementoTipo - Código del elemento tipo
   * @param {Array} procesos - Array de procesos a actualizar
   * @returns {Promise<Object|null>} Resultado de la operación
   */
  async updateProcesos(codigoElementoTipo, procesos) {
    try {
      const result = await api.patch(
        `/${ENTITY_ENDPOINT}/${codigoElementoTipo}/procesos`,
        procesos,
      );
      return result?.data || null;
    } catch (error) {
      console.error(
        `Error al actualizar procesos para elemento tipo ${codigoElementoTipo}:`,
        error,
      );
      throw error;
    }
  },

  async getSeccionesByElementoTipo(codigoElementoTipo, modulo) {
    try {
      const result = await api.get(
        `/${ENTITY_ENDPOINT}/${codigoElementoTipo}/secciones?modulo=${Number(modulo)}`,
      );
      return result?.data || [];
    } catch (error) {
      console.error(`Error al obtener secciones para elemento tipo ${codigoElementoTipo}:`, error);
      throw error;
    }
  },

  async getProcesosSeccionByElementoTipoSeccion(codigoElementoTipo, codigoSeccion, modulo) {
    try {
      const result = await api.get(
        `/${ENTITY_ENDPOINT}/${codigoElementoTipo}/secciones/${codigoSeccion}/${modulo}`,
      );
      return result?.data || [];
    } catch (error) {
      console.error(
        `Error al obtener secciones para elemento tipo ${codigoElementoTipo}, sección ${codigoSeccion}, módulo ${modulo}:`,
        error,
      );
      throw error;
    }
  },

  async updateConfiguracion(codigoElementoTipo, configuracion) {
    return api
      .patch(`/${ENTITY_ENDPOINT}/${codigoElementoTipo}/configuracion`, configuracion)
      .then((response) => response.data)
      .catch((error) => {
        console.error(
          `Error al actualizar configuración para elemento tipo ${codigoElementoTipo}:`,
          error,
        );
        throw error;
      });
  },

  async getConfiguracion(codigoElementoTipo) {
    return api
      .get(`/${ENTITY_ENDPOINT}/${codigoElementoTipo}/configuracion`)
      .then((response) => response.data)
      .catch((error) => {
        console.error(
          `Error al obtener configuración para elemento tipo ${codigoElementoTipo}:`,
          error,
        );
        throw error;
      });
  },
};

export default elementoTipoService;
